<template>
    <div class="sub-form">

        <!-- Skeleton -->
        <template v-if="initLoading">
            <div class="card co-skeleton">
                <div class="co-skel-line co-skel-line--title"></div>
                <div class="co-skel-line"></div>
                <div class="co-skel-line"></div>
                <div class="co-skel-line"></div>
                <div class="co-skel-line co-skel-line--short"></div>
            </div>
            <div class="co-skel-btn"></div>
        </template>

        <!-- Trial already used (slug trial, у юзера уже была подписка в любом статусе) -->
        <template v-else-if="wasSubscription && tier === 'trial'">
            <div class="card sub-trial-used">
                <div class="sub-trial-used-icon">✓</div>
                <div class="sub-trial-used-title">Trial already used</div>
                <div class="sub-trial-used-sub">
                    You've already used your free trial on this account. Choose a paid plan to continue.
                </div>
                <a :href="baseURL + '/pricing'" class="btn-primary sub-trial-used-btn">
                    View paid plans →
                </a>
            </div>
        </template>

        <!-- Already subscribed - manage in account -->
        <template v-else-if="redirectToAccount">
            <div class="card sub-trial-used">
                <div class="sub-trial-used-icon">✓</div>
                <div class="sub-trial-used-title">You already have a subscription</div>
                <div class="sub-trial-used-sub">
                    Manage your plan, upgrade, or view billing right from your account.
                </div>
                <a :href="accountUrl + '/me/subscription'" class="btn-primary sub-trial-used-btn">
                    Go to my account →
                </a>
            </div>
        </template>

        <!-- Form -->
        <template v-else>

            <!-- Contact details - только для гостей (email). Address собирает Stripe. -->
            <div class="card" v-if="!isAuth">
                <div class="section-title" style="margin-bottom:14px">Contact details</div>

                <div class="fg">
                    <input v-model="form.email" @blur="checkEmail" class="fi" :class="{ 'has-error': errors.email }"
                           type="email" id="sub-email" placeholder="Email" autocomplete="email">
                    <label class="fl" for="sub-email">Email<span class="req">*</span></label>
                </div>
                <div v-if="errors.email" class="errors">
                    <p>Email is required</p>
                </div>

                <div v-if="emailHasSub" class="sub-email-notice">
                    An account with this email already has a subscription.
                    <a :href="accountUrl + '/login'">Log in to manage it →</a>
                </div>
            </div>

            <!-- Payment - dynamic subcomponent per gateway -->
            <component
                :is="paymentComponent"
                v-if="paymentComponent && publicKey"
                ref="payment"
                :public-key="publicKey"
                :saved-cards="savedCards"
                :amount="price"
                :slug="subscriptionSlug"
                :tier="tier"
                :contact="contactPayload"
                :is-auth="isAuth"
                @error="onPaymentError"
                @loading="onPaymentLoading"
            ></component>

            <!-- CTA -->
            <div class="cta-wrap">
                <button
                    class="btn-primary"
                    :disabled="isLoading || !paymentComponent || emailHasSub"
                    @click="proceedToPayment"
                >
                    <span v-if="isLoading" class="co-spinner"></span>
                    <span v-else>
                        {{ ctaText }}
                    </span>
                </button>

                <div v-if="globalError" class="co-error">{{ globalError }}</div>

                <!-- Trust -->
                <div class="trust">
                    <div v-if="isTrial" class="trust-line">
                        <strong>Free for 7 days</strong>, then <strong>$8.99/mo.</strong> Cancel anytime.
                    </div>
                    <div v-else class="trust-line">
                        Secure payment · Instant activation
                    </div>
                    <div class="trust-bottom">
                        <div class="tb">
                            <div class="tb-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g opacity="0.5">
                                        <path d="M17 11H7C5.89543 11 5 11.8954 5 13V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V13C19 11.8954 18.1046 11 17 11Z" fill="#E36E00" fill-opacity="0.1" stroke="#E36E00" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M8 11V7C8 5.93913 8.42143 4.92172 9.17157 4.17157C9.92172 3.42143 10.9391 3 12 3C13.0609 3 14.0783 3.42143 14.8284 4.17157C15.5786 4.92172 16 5.93913 16 7V11" stroke="#E36E00" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M12 17.5C12.8284 17.5 13.5 16.8284 13.5 16C13.5 15.1716 12.8284 14.5 12 14.5C11.1716 14.5 10.5 15.1716 10.5 16C10.5 16.8284 11.1716 17.5 12 17.5Z" fill="#E36E00" fill-opacity="0.4"/>
                                    </g>
                                </svg>
                            </div>
                            <div>
                                <div class="tb-title">SSL Secure payment</div>
                                <div class="tb-sub">256-bit encryption</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </template>

    </div>
</template>

<script>
import PaymentStripeSubscribe from './PaymentStripeSubscribe.vue';
import { trackEcommerce, gtmItem, GTM_EVENTS } from '../../js/utils/gtm';

export default {
    name: 'SubscribeApp',

    components: {
        PaymentStripeSubscribe,
    },

    props: {
        subscriptionId   : { type: Number,  required: true },
        subscriptionSlug : { type: String,  required: true },
        tier             : { type: String,  required: true },
        price            : { type: Number,  required: true },
        isAuth           : { type: Boolean, default: false },
    },

    data() {
        return {
            initLoading       : true,
            redirectToAccount : false,
            wasSubscription   : false,
            baseURL           : window.baseURL || '',
            accURL            : window.accURL  || '',
            accountUrl        : window.accURL || '',
            emailChecking     : false,
            emailHasSub       : false,
            form: {
                first_name  : '',
                last_name   : '',
                email       : '',
                phone       : '',
                country     : 'US',
                address     : '',
                address2    : '',
                city        : '',
                state       : '',
                postal_code : '',
            },

            errors      : {},
            globalError : '',
            isLoading   : false,

            // Из init response
            publicKey   : '',
            savedCards  : [],
            gateway     : '',
        };
    },

    computed: {

        // Динамический subcomponent по типу gateway из init
        paymentComponent() {

            if( this.gateway === 'stripe' )
                return 'PaymentStripeSubscribe';

            // future: 'PaymentPayPalSubscribe' etc.

            return null;
        },

        isTrial() {
            return this.tier === 'trial';
        },

        // Для auth не отправляем contact — бэк возьмёт email из $this->auth
        contactPayload() {
            return this.form;
        },

        formatPrice() {
            return '$' + ( this.price / 100 ).toFixed( 2 );
        },

        ctaText() {

            if( this.isTrial )
                return 'Start Free Trial';

            return 'Pay ' + this.formatPrice;
        },
    },

    mounted() {
        this.init();
        window.addEventListener( 'pageshow', this.resetLoader );
    },

    beforeUnmount() {
        window.removeEventListener( 'pageshow', this.resetLoader );
    },

    methods: {

        resetLoader( event ) {
            if( event.persisted )
                this.isLoading = false;
        },

        async init() {

            try {
                const response = await this.$http.post('/subscribe/init', {
                    slug: this.subscriptionSlug,
                });
                const { status, data } = response?.data || {};
                if( status !== 'success' )
                    throw new Error( data?.message || 'Init failed' );

                // Повторный триал: у юзера подписка была когда-либо (блок «Trial already used»)
                this.wasSubscription = data.was_subscription || false;

                // Case 1: auth с не финальной подпиской - управление в account
                this.redirectToAccount = data.redirect_to_account || false;

                if( this.redirectToAccount || ( this.wasSubscription && this.tier === 'trial' ) ) {
                    this.accountUrl = data.account_url || this.accURL;
                    setTimeout(() => this.initLoading = false, 300);
                    return;
                }

                // Prefill для всех (для Stripe Element)
                if( data.user ) {

                    this.form.email       = data.user.email       || '';
                    this.form.country     = data.user.country     || 'US';
                    this.form.postal_code = data.user.postal_code || '';
                    this.form.phone       = data.user.phone       || '';

                    // Address fields - только для гостей (auth их не вводит)
                    if( ! this.isAuth ) {

                        const nameParts = ( data.user.name || '' ).trim().split(' ');

                        this.form.first_name = nameParts[0] || '';
                        this.form.last_name  = nameParts.slice(1).join(' ') || '';
                        this.form.address    = data.user.address  || '';
                        this.form.address2   = data.user.address2 || '';
                        this.form.city       = data.user.city     || '';
                        this.form.state      = data.user.state    || '';
                    }
                }

                this.publicKey  = data.stripe_pub_key || '';
                this.savedCards = data.saved_cards || [];
                this.gateway    = data.gateway || '';

                // ④ begin_checkout (subscribe-intent) - на открытии страницы оформления.
                trackEcommerce( GTM_EVENTS.BEGIN_CHECKOUT, {
                    currency : 'USD',
                    value    : +( this.price / 100 ).toFixed( 2 ),
                    items    : [ this.planItem() ],
                }, {
                    plan         : this.planTitle(),
                    payment_type : 'card',
                } );

            } catch (e) {
                console.error('Subscribe init failed:', e);
            } finally {
                setTimeout( () => this.initLoading = false, 500 );
            }
        },

        // Канон регистра плана - title ("Starter"), как в select_item/subscribe (F-13).
        planTitle() {
            return this.tier
                ? this.tier.charAt( 0 ).toUpperCase() + this.tier.slice( 1 )
                : this.subscriptionSlug;
        },

        // WP1 канон-айтем плана. id=slug, name=title, category='subscription'.
        planItem() {
            return gtmItem( {
                id       : this.subscriptionSlug,
                name     : this.planTitle(),
                category : 'subscription',
                price    : +( this.price / 100 ).toFixed( 2 ),
            } );
        },

        async checkEmail() {

            const email = this.form.email?.trim();

            // Пустой / невалидный / auth - не проверяем
            if( this.isAuth || ! email || ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( email ) ) {
                this.emailHasSub = false;
                return;
            }

            this.emailChecking = true;

            try {
                const response = await this.$http.post( '/subscribe/checkEmail', { email } );
                const { status, data } = response?.data || {};

                if( status === 'success' ) {
                    this.emailHasSub = data.has_subscription || false;
                    if( data.account_url )
                        this.accountUrl = data.account_url;
                }
            } catch( e ) {
                this.emailHasSub = false;   // fail-open: intent отловит case 2 как 409 backup
            } finally {
                this.emailChecking = false;
            }
        },

        async proceedToPayment() {

            if( this.emailHasSub ) {
                this.globalError = 'Please log in to manage your existing subscription.';
                return;
            }

            // Validate contact только для гостей
            if( ! this.isAuth && ! this.validate() )
                return;

            this.globalError = '';

            // ④ add_payment_info - пользователь подтвердил оформление подписки.
            // begin_checkout шлётся на init страницы.
            trackEcommerce( GTM_EVENTS.ADD_PAYMENT_INFO, {
                currency : 'USD',
                value    : +( this.price / 100 ).toFixed( 2 ),
                items    : [ this.planItem() ],
            }, {
                plan         : this.planTitle(),
                payment_type : 'card',
            } );

            // Передаём управление subcomponent'у
            if( this.$refs.payment?.submit )
                await this.$refs.payment.submit();
        },

        validate() {

            const e = {};
            const f = this.form;
            const req = v => !v || !v.trim();

            if( req( f.email ) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( f.email ) )
                e.email = true;

            this.errors = e;
            return Object.keys( e ).length === 0;
        },

        onPaymentError( message ) {
            this.globalError = message || 'Payment failed';
        },

        onPaymentLoading( isLoading ) {
            this.isLoading = isLoading;
        },
    },
};
</script>